import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Box,
  Alert,
  TextField,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "bootstrap/dist/css/bootstrap.min.css";
import apiService, {
  parseSupportTicketsListResponse,
} from "../../services/api";
import { DATE_FORMATS } from "../../constants";
import { useUserDetails } from "../../contexts/UserDetailsContext";
import { useNotification } from "../../contexts/NotificationContext";
import useCodeCommon from "../../hooks/useCodeCommon";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
import {
  findExpiredPromoStatusCodeValue,
  resolvePromoStatusCodeValue,
} from "../marketing/promoStatusUtils";
import { KYCData, KYCResponse } from "../../types";
import CustomLoading from "../../components/common/CustomLoading";
import {
  formatMobileNumber,
  formatTransactionIdAsIST,
} from "../../utils/formatters";
import DefaultImage from "../../assets/images/DefaultImage.png";
import {
  resolvePreviewSrc as resolvePreviewSrcUtil,
  parseFilePaths,
  isPdfFile,
} from "../../utils/mediaUtils";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import EditIcon from "../../assets/icons/EditIcon.svg";
import DeleteIcon from "../../assets/icons/DeleteIcon.svg";
import DeleteConfirmDialog from "../../components/common/DeleteConfirmDialog";
import { getUserData } from "../../utils/cookieUtils";

// Wrapper function that uses the local DefaultImage asset
const resolvePreviewSrc = (value: string | null | undefined): string => {
  return resolvePreviewSrcUtil(value, DefaultImage);
};

interface UserDetails {
  id: string;
  userName: string;
  mobileNo: string;
  email: string;
  status: string;
  kyc: string;
  totalTrips: number;
  joined: string;
  lastLogin?: string;
  totalPaid?: number;
  outstanding?: number;
  refunds?: number;
  roleName?: string;
  hubName?: string;
  isBanned?: boolean;
  bannedReason?: string;
  dob?: string;
  empCode?: string;
  userjoiningdate?: string;
  /** Mobile app version from user profile API (`appversion`). */
  appVersion?: string;
  rideHistory?: Array<{
    evModel: string;
    tripStartDate: string;
    tripEndDate: string;
    distance: string;
    amountPaid: string;
  }>;
  supportTickets?: Array<{
    ticketId: string;
    ticketType: string;
    raisedDate: string;
    status: string;
  }>;
}

interface SupportTicket {
  ticketmasterid: number;
  ticketid: string;
  userid: number;
  enteredon: string;
  username: string;
  phoneno: string;
  ticketcategoryid: number;
  ticketpriorityid: number;
  statusid: number;
  hubmasterid: number;
  hubname: string;
  assignee: number;
  description: string;
  medias: string;
  bookingid: number;
}

const sectionTitleSx = {
  fontSize: "18px",
  fontWeight: "bold",
  color: "#333",
  mb: 2,
};

const labelSx = {
  fontSize: "13px",
  fontWeight: "bold",
  color: "rgba(80, 80, 80, 1)",
  mb: 0.5,
};

const valueSx = {
  fontSize: "15px",
  color: "#333",
  fontWeight: "bold",
};

const tableHeaderSx = {
  fontWeight: "bold",
  fontSize: "15px",
  color: "#333",
  py: 1.5,
};

const tableCellSx = {
  fontSize: "15px",
  color: "#333",
  py: 1.5,
};

// StatusChip component matching UserManagementPage
const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
  Banned: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
  Absconded: { bg: "rgba(255, 235, 205, 1)", color: "#FF3B30" },
  TemporarilyDeleted: {
    bg: "rgba(255, 244, 229, 1)",
    color: "rgba(178, 106, 0, 1)",
  },
  PermanentlyDeleted: {
    bg: "rgba(238, 238, 238, 1)",
    color: "rgba(97, 97, 97, 1)",
  },
};

const cardSx = {
  borderRadius: 1,
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  mb: 3,
};

function formatRupeePromo(n: unknown): string {
  if (n == null || n === "") return "—";
  const num = typeof n === "number" ? n : Number.parseFloat(String(n));
  return Number.isFinite(num) ? `₹${num.toLocaleString("en-IN")}` : String(n);
}

function promoDiscountValueDisplay(item: Record<string, unknown>): string {
  const typeNum = item.discounttype ?? item.discountType;
  const valRaw = item.discountvalue ?? item.discountValue;
  const num =
    typeof valRaw === "number"
      ? valRaw
      : Number.parseFloat(String(valRaw ?? ""));
  const isPercent = typeNum === 2 || typeNum === "2";
  if (isPercent) return Number.isFinite(num) ? `${num}%` : "—";
  return formatRupeePromo(valRaw);
}

function promoValidityDisplay(item: Record<string, unknown>): string {
  const start = item.startdate ?? item.startDate;
  const end =
    item.expirydate ?? item.expiryDate ?? item.enddate ?? item.endDate;
  const a = start ? DATE_FORMATS.formatDate(String(start)) : "";
  const b = end ? DATE_FORMATS.formatDate(String(end)) : "";
  if (a && b) return `${a} - ${b}`;
  if (a) return a;
  if (b) return b;
  return "—";
}

function couponCouponCode(item: Record<string, unknown>): string {
  const raw =
    item.couponname ??
    item.couponName ??
    item.qrcodelink ??
    item.qrCodeLink ??
    item.codename ??
    "";
  const s = String(raw).trim();
  return s || "—";
}

function promoCouponIdString(item: Record<string, unknown>): string | null {
  const raw = item.couponid ?? item.couponId;
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  return s !== "" ? s : null;
}

const couponStatusChipSx = (statusLabel: string) => {
  const lower = statusLabel.toLowerCase();
  const isPositive =
    !/(expir|inactive|void|cancel|closed|disabled|delet)/i.test(lower);
  return {
    fontWeight: 600 as const,
    backgroundColor: isPositive
      ? "rgba(212, 255, 199, 1)"
      : "rgba(255, 227, 227, 1)",
    color: isPositive ? "rgba(31, 113, 13, 1)" : "rgba(197, 0, 0, 1)",
  };
};

const SAVE_PROMO_FALLBACK_ERROR =
  "Failed to save promo code. Please try again.";

function getSavePromoApiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object" && data !== null) {
    const msg = (data as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim() !== "") return msg.trim();
  }
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  return SAVE_PROMO_FALLBACK_ERROR;
}

/** GET `GetPromoCodeById` — `{ statusCode, isSuccess, message, data }`. */
function parsePromoCodeByIdResponse(res: unknown): {
  ok: boolean;
  data: Record<string, unknown> | null;
  message?: string;
} {
  if (res == null || typeof res !== "object") {
    return { ok: false, data: null };
  }
  const r = res as {
    isSuccess?: boolean;
    success?: boolean;
    statusCode?: number;
    message?: string;
    data?: unknown;
  };
  if (r.isSuccess === false) {
    return {
      ok: false,
      data: null,
      message: typeof r.message === "string" ? r.message : undefined,
    };
  }
  const d = r.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    return { ok: true, data: d as Record<string, unknown> };
  }
  return {
    ok: false,
    data: null,
    message: typeof r.message === "string" ? r.message : undefined,
  };
}

const issueCouponModalFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#dee2e6" },
    borderRadius: 1,
  },
};

const issueCouponHeaderBlue = "rgba(22, 67, 135, 1)";
const issueCouponSubmitYellow = "#FFEB3B";

/** Primary yellow action — matches Issue Coupon dialog Submit / Update */
const issueCouponPrimaryButtonSx = {
  backgroundColor: issueCouponSubmitYellow,
  color: issueCouponHeaderBlue,
  fontWeight: 700,
  textTransform: "none" as const,
  px: 2.5,
  py: 1,
  minHeight: 40,
  borderRadius: 1,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#FDD835",
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "rgba(255, 235, 59, 0.5)",
    color: "rgba(22, 67, 135, 0.6)",
  },
};

const infoBoxSx = {
  width: 100,
  height: 60,
  border: "1px solid #dee2e6",
  borderRadius: 1,
  bgcolor: "#f8f9fa",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "10px",
  color: "text.secondary",
};

const UserDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("id");
  const { setCurrentUserName } = useUserDetails();
  const { showNotification } = useNotification();

  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<any[]>([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [kycList, setKycList] = useState<KYCData[]>([]);
  const [kycData, setKycData] = useState<KYCData | null>(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycApproving, setKycApproving] = useState(false);
  const [approveKycDialogOpen, setApproveKycDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  // Get ticket categories and statuses from common codes
  const { options: ticketCategories } = useCodeCommon("TicketCategory");
  const { options: ticketStatuses } = useCodeCommon("SupportTicketStatus");

  // Get user status codes (same as UserManagementPage)
  const {
    getDisplayNameByCodeValue,
    statusCodes,
    statusOptions,
    getCodeValueByDisplayName,
  } = useCodeCommonByCategory("UserStatus");
  const {
    getDisplayNameByCodeValue: getPromoStatusDisplayName,
    statusCodes: promoStatusCodes,
  } = useCodeCommonByCategory("PromoCodeStatus");
  const expiredPromoStatusCodeValue = useMemo(
    () => findExpiredPromoStatusCodeValue(promoStatusCodes),
    [promoStatusCodes],
  );
  const {
    getDisplayNameByCodeValue: getDiscountTypeDisplayName,
    statusCodes: couponDiscountTypeCodes,
  } = useCodeCommonByCategory("CouponDiscountType");

  // Store raw user data to update status after codes are loaded
  const [rawUserData, setRawUserData] = useState<any>(null);
  const canManageUserCoupons = useMemo(() => {
    const loggedInUserData = getUserData();
    const normalizedRole = String(loggedInUserData?.rolename ?? "")
      .toLowerCase()
      .trim();
    return [
      "superadmin",
      "admin",
      "hub manager",
      "hub cluster manager",
    ].includes(normalizedRole);
  }, []);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSuccess, setStatusSuccess] = useState<string | null>(null);

  const [issueCouponOpen, setIssueCouponOpen] = useState(false);
  const [issueCouponCode, setIssueCouponCode] = useState("");
  const [issueDiscountType, setIssueDiscountType] = useState<string | number>(
    "",
  );
  const [issueDiscountValue, setIssueDiscountValue] = useState("");
  const [issueExpiryDate, setIssueExpiryDate] = useState<Dayjs | null>(null);
  const [issueUsageLimit, setIssueUsageLimit] = useState("");
  const [issueReason, setIssueReason] = useState("");
  const [issueCouponSubmitting, setIssueCouponSubmitting] = useState(false);
  /** `null` = issue new coupon; set after successful GetPromoCodeById when editing. */
  const [issueCouponEditingId, setIssueCouponEditingId] = useState<
    number | null
  >(null);
  /** Snapshot from GetPromoCodeById for fields not shown in the modal (start date, min booking, flags). */
  const [issueCouponDetail, setIssueCouponDetail] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [issueCouponDetailLoading, setIssueCouponDetailLoading] =
    useState(false);
  /** True while opening edit (before `issueCouponEditingId` is set) so the title shows "Edit Coupon" during fetch. */
  const [issueCouponEditIntent, setIssueCouponEditIntent] = useState(false);

  const [userPromoCodes, setUserPromoCodes] = useState<
    Record<string, unknown>[]
  >([]);
  const [userPromoCodesLoading, setUserPromoCodesLoading] = useState(false);
  const [userPromoCodesError, setUserPromoCodesError] = useState<string | null>(
    null,
  );

  const resetIssueCouponForm = useCallback(() => {
    setIssueCouponCode("");
    setIssueDiscountType("");
    setIssueDiscountValue("");
    setIssueExpiryDate(null);
    setIssueUsageLimit("");
    setIssueReason("");
    setIssueCouponEditingId(null);
    setIssueCouponDetail(null);
    setIssueCouponDetailLoading(false);
    setIssueCouponEditIntent(false);
  }, []);

  const handleCloseIssueCoupon = useCallback(() => {
    setIssueCouponOpen(false);
    resetIssueCouponForm();
  }, [resetIssueCouponForm]);

  const openIssueCouponCreate = useCallback(() => {
    resetIssueCouponForm();
    setIssueCouponOpen(true);
  }, [resetIssueCouponForm]);

  const openEditIssueCoupon = useCallback(
    async (couponId: string) => {
      setIssueCouponEditIntent(true);
      setIssueCouponOpen(true);
      setIssueCouponDetailLoading(true);
      setIssueCouponEditingId(null);
      setIssueCouponDetail(null);
      try {
        const res = await apiService.getPromoCodeById(couponId);
        const parsed = parsePromoCodeByIdResponse(res);
        if (!parsed.ok || !parsed.data) {
          showNotification(
            parsed.message?.trim() || "Could not load promo code.",
            "error",
          );
          handleCloseIssueCoupon();
          return;
        }
        const d = parsed.data;
        const idNum = Number(d.couponid ?? d.couponId);
        if (!Number.isFinite(idNum)) {
          showNotification("Invalid promo code id.", "error");
          handleCloseIssueCoupon();
          return;
        }
        setIssueCouponEditingId(idNum);
        setIssueCouponDetail(d);
        setIssueCouponCode(String(d.couponname ?? d.couponName ?? ""));

        const discountTypeRaw = d.discounttype ?? d.discountType;
        const discountTypeValue =
          typeof discountTypeRaw === "number" ||
          typeof discountTypeRaw === "string"
            ? String(discountTypeRaw)
            : "";
        setIssueDiscountType(discountTypeValue);
        setIssueDiscountValue(String(d.discountvalue ?? d.discountValue ?? ""));
        const exp = d.expirydate ?? d.expiryDate;
        setIssueExpiryDate(exp ? dayjs(String(exp)) : null);
        setIssueUsageLimit(String(d.usagelimit ?? d.usageLimit ?? ""));
        setIssueReason(String(d.reason ?? ""));
      } catch (e: unknown) {
        showNotification(
          e instanceof Error ? e.message : "Failed to load promo code.",
          "error",
        );
        handleCloseIssueCoupon();
      } finally {
        setIssueCouponDetailLoading(false);
      }
    },
    [showNotification, handleCloseIssueCoupon],
  );

  const fetchUserPromoCodes = useCallback(() => {
    if (!userId) {
      setUserPromoCodes([]);
      setUserPromoCodesError(null);
      setUserPromoCodesLoading(false);
      return;
    }
    const uid = Number(userId);
    if (!Number.isFinite(uid) || uid <= 0) return;
    setUserPromoCodesLoading(true);
    setUserPromoCodesError(null);
    apiService
      .getPromoCodesByUserId(uid)
      .then((res: unknown) => {
        const wrap = res as {
          isSuccess?: boolean;
          message?: string;
          data?: unknown;
        };
        if (wrap && typeof wrap === "object" && wrap.isSuccess === false) {
          setUserPromoCodes([]);
          setUserPromoCodesError(
            typeof wrap.message === "string"
              ? wrap.message
              : "Could not load promo codes.",
          );
          return;
        }
        const raw = (wrap as { data?: unknown })?.data;
        const list = Array.isArray(raw) ? raw : [];
        setUserPromoCodes(
          list.filter(
            (x): x is Record<string, unknown> =>
              x != null && typeof x === "object" && !Array.isArray(x),
          ),
        );
        setUserPromoCodesError(null);
      })
      .catch((e: unknown) => {
        setUserPromoCodes([]);
        setUserPromoCodesError(
          e instanceof Error ? e.message : "Failed to load promo codes.",
        );
      })
      .finally(() => {
        setUserPromoCodesLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    fetchUserPromoCodes();
  }, [fetchUserPromoCodes]);

  const [couponDeleteDialogOpen, setCouponDeleteDialogOpen] = useState(false);
  const [couponPendingDelete, setCouponPendingDelete] = useState<{
    id: string;
    codeName: string;
  } | null>(null);
  const [couponDeleteLoading, setCouponDeleteLoading] = useState(false);

  const openDeleteUserCoupon = useCallback((row: Record<string, unknown>) => {
    const id = promoCouponIdString(row);
    if (!id) return;
    setCouponPendingDelete({ id, codeName: couponCouponCode(row) });
    setCouponDeleteDialogOpen(true);
  }, []);

  const cancelDeleteUserCoupon = useCallback(() => {
    if (couponDeleteLoading) return;
    setCouponDeleteDialogOpen(false);
    setCouponPendingDelete(null);
  }, [couponDeleteLoading]);

  const confirmDeleteUserCoupon = useCallback(async () => {
    if (!couponPendingDelete?.id) return;
    setCouponDeleteLoading(true);
    try {
      await apiService.deletePromoCode(couponPendingDelete.id);
      showNotification("Promo code deleted successfully.");
      setCouponDeleteDialogOpen(false);
      setCouponPendingDelete(null);
      fetchUserPromoCodes();
    } catch (e: unknown) {
      showNotification(
        e instanceof Error ? e.message : "Failed to delete promo code.",
        "error",
      );
    } finally {
      setCouponDeleteLoading(false);
    }
  }, [couponPendingDelete, showNotification, fetchUserPromoCodes]);

  const handleSubmitIssueCoupon = useCallback(async () => {
    const uid = userId ? Number(userId) : NaN;
    if (!userId || !Number.isFinite(uid) || uid <= 0) {
      showNotification(
        "Invalid user. Please open this page from a user profile.",
        "error",
      );
      return;
    }

    const name = issueCouponCode.trim();
    if (!name) {
      showNotification("Enter a coupon code.", "error");
      return;
    }
    if (
      issueDiscountType === "" ||
      issueDiscountType === null ||
      issueDiscountType === undefined
    ) {
      showNotification("Select a discount type.", "error");
      return;
    }

    const discountVal = Number.parseFloat(
      String(issueDiscountValue).replaceAll(/[₹,\s]/g, ""),
    );
    if (!Number.isFinite(discountVal) || discountVal < 0) {
      showNotification("Enter a valid discount value.", "error");
      return;
    }

    if (!issueExpiryDate || !issueExpiryDate.isValid()) {
      showNotification("Select an expiry date.", "error");
      return;
    }

    const usage = 1;
    if (!Number.isFinite(usage) || usage < 0) {
      showNotification("Enter a valid usage limit (0 or greater).", "error");
      return;
    }

    const startToday = dayjs().startOf("day");
    const expiry = issueExpiryDate.endOf("day");
    const snap = issueCouponDetail;

    if (issueCouponEditingId != null && snap) {
      const startRaw = snap.startdate ?? snap.startDate;
      const startBound = startRaw
        ? dayjs(String(startRaw)).startOf("day")
        : startToday;
      if (expiry.isBefore(startBound, "day")) {
        showNotification(
          "Expiry date must be on or after the start date.",
          "error",
        );
        return;
      }
    } else if (expiry.isBefore(startToday, "day")) {
      showNotification("Expiry date must be today or later.", "error");
      return;
    }

    const discounttype = Number(issueDiscountType);
    if (!Number.isFinite(discounttype)) {
      showNotification("Select a valid discount type.", "error");
      return;
    }

    let payload: Record<string, unknown>;

    if (issueCouponEditingId != null && snap) {
      const startRaw = snap.startdate ?? snap.startDate;
      const startIso = startRaw
        ? dayjs(String(startRaw)).toISOString()
        : startToday.toISOString();
      const minRaw = snap.minbookingamount ?? snap.minBookingAmount;
      const minBook = Number(minRaw);
      const snapUser = snap.userid ?? snap.userId;
      const useridPayload =
        snapUser != null && String(snapUser).trim() !== ""
          ? Number(snapUser)
          : uid;

      payload = {
        couponid: issueCouponEditingId,
        couponname: name,
        description: String(snap.description ?? ""),
        discounttype,
        discountvalue: discountVal,
        minbookingamount: Number.isFinite(minBook) ? minBook : 0,
        startdate: startIso,
        expirydate: expiry.toISOString(),
        usagelimit: usage,
        qrcodelink: String(snap.qrcodelink ?? snap.qrCodeLink ?? name),
        userid: Number.isFinite(useridPayload) ? useridPayload : uid,
        reason: issueReason.trim(),
        status: Number(snap.status ?? 1),
        isreferralcoupon: Boolean(
          snap.isreferralcoupon ?? snap.isReferralCoupon ?? false,
        ),
        is_user_assignable: Boolean(
          snap.is_user_assignable ?? snap.isUserAssignable ?? false,
        ),
        is_coupon_reuseable: Boolean(
          snap.is_coupon_reuseable ?? snap.isCouponReuseable ?? false,
        ),
      };
    } else {
      payload = {
        couponid: 0,
        couponname: name,
        description: "",
        discounttype,
        discountvalue: discountVal,
        minbookingamount: 0,
        startdate: startToday.toISOString(),
        expirydate: expiry.toISOString(),
        usagelimit: usage,
        qrcodelink: name,
        userid: uid,
        reason: issueReason.trim(),
        status: 1,
        isreferralcoupon: false,
        is_user_assignable: false,
        is_coupon_reuseable: false,
      };
    }

    setIssueCouponSubmitting(true);
    try {
      const res = (await apiService.savePromoCode(payload)) as {
        isSuccess?: boolean;
        message?: string;
      };
      if (res && typeof res === "object" && res.isSuccess === false) {
        showNotification(
          typeof res.message === "string" && res.message.trim() !== ""
            ? res.message.trim()
            : "Could not save promo code.",
          "error",
        );
        return;
      }
      const defaultMsg =
        issueCouponEditingId != null
          ? "Promo code updated successfully."
          : "Promo code saved successfully.";
      showNotification(
        typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : defaultMsg,
        "success",
      );
      handleCloseIssueCoupon();
      fetchUserPromoCodes();
    } catch (e: unknown) {
      showNotification(getSavePromoApiErrorMessage(e), "error");
    } finally {
      setIssueCouponSubmitting(false);
    }
  }, [
    userId,
    issueCouponCode,
    issueCouponEditingId,
    issueCouponDetail,
    issueDiscountType,
    issueDiscountValue,
    issueExpiryDate,
    issueUsageLimit,
    issueReason,
    showNotification,
    handleCloseIssueCoupon,
    fetchUserPromoCodes,
  ]);

  const fetchUserDetails = useCallback(async () => {
    if (!userId || hasFetched.current) return;

    hasFetched.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getUserById(userId);
      const userData = (response as any)?.data || response;

      // Store raw data
      setRawUserData(userData);

      // Transform the API response to match our interface
      // Get status display name from codevalue using the status codes (same as UserManagementPage)
      const statusDisplayName =
        getDisplayNameByCodeValue(userData?.userstatus) || "";

      const transformedUser: UserDetails = {
        id: userData?.userid?.toString() || userId,
        userName: userData?.name || "",
        mobileNo: formatMobileNumber(userData?.phonenumber) || "",
        email: userData?.emailid || "",
        status: statusDisplayName,
        kyc: userData?.kycstatus || "Pending",
        totalTrips: userData?.totalTrips || 0,
        joined: DATE_FORMATS.formatDate(userData?.createddate || ""),
        roleName: userData?.rolename || "",
        hubName: userData?.hubname || "",
        isBanned: userData?.isbanned || false,
        bannedReason: userData?.bannedreason || "",
        dob: userData?.dob || "",
        empCode: userData?.empcode || "",
        userjoiningdate: DATE_FORMATS.formatDateTime(
          userData?.userjoiningdate || "",
        ),
        appVersion:
          userData?.appversion != null &&
          String(userData.appversion).trim() !== ""
            ? String(userData.appversion).trim()
            : "",
        rideHistory: userData?.rideHistory,
      };

      setUserDetails(transformedUser);
      // Set the user name in context for Header component
      setCurrentUserName(transformedUser.userName);
      setSelectedStatus(statusDisplayName);
    } catch (err: any) {
      console.error("Error fetching user details:", err);
      setError(err.message || "Failed to fetch user details");
      setCurrentUserName(null);
    } finally {
      setLoading(false);
    }
  }, [userId, setCurrentUserName, getDisplayNameByCodeValue]);

  // Update status when status codes are loaded
  useEffect(() => {
    if (rawUserData && statusCodes.length > 0) {
      const statusDisplayName =
        getDisplayNameByCodeValue(rawUserData?.userstatus) || "";
      if (statusDisplayName) {
        setUserDetails((prev) => {
          if (!prev) return null;
          if (prev.status !== statusDisplayName) {
            return { ...prev, status: statusDisplayName };
          }
          return prev;
        });
        setSelectedStatus(statusDisplayName);
      }
    }
  }, [statusCodes, rawUserData, getDisplayNameByCodeValue]);

  const handleSaveStatus = async () => {
    if (
      !userDetails ||
      !selectedStatus ||
      selectedStatus === userDetails.status
    ) {
      return;
    }

    setIsSavingStatus(true);
    setStatusError(null);
    setStatusSuccess(null);

    try {
      const codeValue = getCodeValueByDisplayName(selectedStatus);
      if (!codeValue) {
        setStatusError("Invalid status selected.");
        setIsSavingStatus(false);
        return;
      }

      const userstatus = Number.parseInt(codeValue, 10);
      await apiService.updateUserStatus(userstatus, userDetails.id);

      setUserDetails((prev) =>
        prev ? { ...prev, status: selectedStatus } : prev,
      );
      setStatusSuccess("Status updated successfully.");
      showNotification("User status updated successfully.", "success");
      navigate("/user-management", { state: location.state });
    } catch (err: any) {
      setStatusError(err.message || "Failed to update status.");
    } finally {
      setIsSavingStatus(false);
    }
  };

  const fetchBookingDetail = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await apiService.getBookingsByUserId(userId);
      const data = (response as any)?.data || [];
      setBookings(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching user bookings:", err);
      setBookings([]);
    }
  }, [userId]);

  const fetchSupportTickets = useCallback(async () => {
    if (!userId) return;
    try {
      const numericUserId = Number.parseInt(String(userId), 10);
      const response = await apiService.searchSupportTickets({
        userId: Number.isFinite(numericUserId) ? numericUserId : 0,
        pageNumber: 1,
        pageSize: 500,
        ticketid: "",
        createdDate: null,
        assignee: 0,
        ticketpriorityid: 0,
        statusid: 0,
        ticketcategoryid: null,
      });
      const { items } = parseSupportTicketsListResponse(response);
      setSupportTickets(Array.isArray(items) ? (items as SupportTicket[]) : []);
    } catch (err: any) {
      console.error("Error fetching support tickets:", err);
      setSupportTickets([]);
    }
  }, [userId]);

  const fetchKYCData = useCallback(async () => {
    if (!userId) return;
    setKycLoading(true);
    try {
      const response = await apiService.getKYCByUserId(userId);
      const kycResponse = response as KYCResponse;
      if (
        kycResponse.isSuccess &&
        Array.isArray(kycResponse.data) &&
        kycResponse.data.length > 0
      ) {
        const list = kycResponse.data;
        setKycList(list);
        const primary =
          list.find((k) => k.kycstatus === 1) ?? list[list.length - 1];
        setKycData(primary);
      } else {
        setKycList([]);
        setKycData(null);
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
    } finally {
      setKycLoading(false);
    }
  }, [userId]);

  const handleApproveKYC = useCallback(async () => {
    const latestAttempt =
      kycList.length > 0 ? kycList[kycList.length - 1] : kycData;
    if (!latestAttempt?.kycid) {
      showNotification("No KYC attempt found to approve.", "error");
      return;
    }

    setKycApproving(true);
    try {
      latestAttempt.kycdetails.status = "manually_approved";
      let req = {
        kycid: latestAttempt.kycid,
        kycstatus: 1,
        kycdetails: latestAttempt.kycdetails,
        userid: userId,
      };
      console.log("req", req);
      const response = (await apiService.createOrUpdateKYC(req)) as {
        isSuccess?: boolean;
        message?: string;
      };

      if (response?.isSuccess === false) {
        showNotification(
          response.message?.trim() || "Failed to manually approve KYC.",
          "error",
        );
        return;
      }

      showNotification("KYC manually approved successfully.", "success");
      await fetchKYCData();
      setUserDetails((prev) => (prev ? { ...prev, kyc: "Verified" } : prev));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to manually approve KYC.";
      showNotification(message, "error");
    } finally {
      setKycApproving(false);
    }
  }, [kycList, kycData, fetchKYCData, showNotification]);

  const openApproveKycDialog = useCallback(() => {
    if (kycApproving) return;
    setApproveKycDialogOpen(true);
  }, [kycApproving]);

  const closeApproveKycDialog = useCallback(() => {
    if (kycApproving) return;
    setApproveKycDialogOpen(false);
  }, [kycApproving]);

  const confirmApproveKyc = useCallback(async () => {
    await handleApproveKYC();
    setApproveKycDialogOpen(false);
  }, [handleApproveKYC]);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchBookingDetail();
      fetchSupportTickets();
      fetchKYCData();
    } else {
      setError("User ID not provided");
      setLoading(false);
    }
  }, [
    userId,
    fetchUserDetails,
    fetchBookingDetail,
    fetchSupportTickets,
    fetchKYCData,
  ]);

  // Cleanup: Clear user name when component unmounts
  useEffect(() => {
    return () => {
      setCurrentUserName(null);
    };
  }, [setCurrentUserName]);

  const handleBack = () => {
    navigate("/user-management", { state: location.state });
  };

  // Derived rows for Ride History from bookings
  const rideRows = useMemo(() => {
    return bookings.map((b) => ({
      evModel: b.vehiclename || b.planname || "-",
      tripStartDate: DATE_FORMATS.formatDateTime(b.pickupstarttime),
      tripEndDate: DATE_FORMATS.formatDateTime(b.bookingPlanModel.planenddate),
      distance: "-",
      amountPaid:
        b?.bookingPlanModel?.bookingamount != null
          ? `₹${b.bookingPlanModel.bookingamount}`
          : "₹0",
    }));
  }, [bookings]);

  // Payments summary derived from bookings
  const paymentsSummary = useMemo(() => {
    const totalPaid = bookings.reduce(
      (sum, b) => sum + (Number(b?.bookingPlanModel?.bookingamount) || 0),
      0,
    );
    return {
      totalPaid,
      outstanding: 0,
      refunds: 0,
    };
  }, [bookings]);

  /** From GetUserById `userbankdetails` (snake/camel). */
  const userBankDetailsDisplay = useMemo(() => {
    const raw =
      rawUserData?.userbankdetails ?? rawUserData?.userBankDetails ?? null;
    if (raw == null || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    const holder = String(o.holdername ?? o.holderName ?? "").trim();
    const bankName = String(o.bankname ?? o.bankName ?? "").trim();
    const account = String(o.accountnumber ?? o.accountNumber ?? "").trim();
    const ifsc = String(o.ifscode ?? o.ifscCode ?? "").trim();
    const upi = String(o.upiid ?? o.upiId ?? "").trim();
    if (!holder && !bankName && !account && !ifsc && !upi) return null;
    return { holder, bankName, account, ifsc, upi };
  }, [rawUserData]);

  // Helper functions for mapping IDs to names using dynamic data
  const getTicketTypeName = (categoryId: number): string => {
    const category = ticketCategories.find(
      (cat) => cat.codevalue === categoryId.toString(),
    );
    return category?.codedisplayname || "Unknown";
  };

  const getStatusName = (statusId: number): string => {
    const status = ticketStatuses.find(
      (stat) => stat.codevalue === statusId.toString(),
    );
    return status?.codedisplayname || "Unknown";
  };

  // Transform support tickets data to match UI format
  const transformedSupportTickets = useMemo(() => {
    return supportTickets.map((ticket) => ({
      ticketId: ticket.ticketid,
      ticketType: getTicketTypeName(ticket.ticketcategoryid),
      raisedDate: DATE_FORMATS.formatDate(ticket.enteredon),
      status: getStatusName(ticket.statusid),
    }));
  }, [supportTickets, ticketCategories, ticketStatuses]);

  if (loading) {
    return <CustomLoading message="Fetching User Details..." />;
  }

  if (error) {
    return (
      <Box className="container-fluid p-1">
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={handleBack}
          sx={{
            px: 4,
            py: 1.2,
            fontSize: "15px",
            borderRadius: 1.5,
            textTransform: "none",
            backgroundColor: "rgba(13, 44, 90, 1)",
            "&:hover": {
              backgroundColor: "rgba(13, 44, 90, 0.8)",
            },
          }}
        >
          Back to User Management
        </Button>
      </Box>
    );
  }

  if (!userDetails) {
    return (
      <Box className="container-fluid p-1">
        <Alert severity="warning" className="mb-3">
          User details not found
        </Alert>
        <Button
          variant="contained"
          onClick={handleBack}
          sx={{
            px: 4,
            py: 1.2,
            fontSize: "15px",
            borderRadius: 1.5,
            textTransform: "none",
            backgroundColor: "rgba(13, 44, 90, 1)",
            "&:hover": {
              backgroundColor: "rgba(13, 44, 90, 0.8)",
            },
          }}
        >
          Back to User Management
        </Button>
      </Box>
    );
  }

  return (
    <Box className="container-fluid p-1">
      {/* Basic Info */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="h6"
              sx={{ ...sectionTitleSx, color: "rgba(22, 67, 135, 1)", mb: 0 }}
            >
              Basic Information
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "rgba(120, 120, 120, 1)",
                }}
              >
                Status
              </Typography>
              <TextField
                select
                size="small"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setStatusError(null);
                  setStatusSuccess(null);
                }}
                SelectProps={{
                  renderValue: (value) => {
                    if (!value) return "";
                    return (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                        }}
                      >
                        {value as string}
                      </Typography>
                    );
                  },
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        width: 160,
                        p: 0,
                        borderRadius: 1,
                        "& .MuiMenu-list": {
                          p: 0,
                        },
                      },
                    },
                  },
                }}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": {
                    height: 32,
                    borderRadius: 1,
                    px: 1,
                    "& fieldset": {
                      borderColor: "rgba(200, 200, 200, 1)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(22, 67, 135, 0.8)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(22, 67, 135, 1)",
                      borderWidth: 1,
                    },
                  },
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      border: "1px solid rgba(220, 220, 220, 1)",
                      borderRadius: 0,
                      "&:not(:last-of-type)": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              {kycData?.kycstatus === 2 ? (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "rgba(22, 135, 37, 0.8)",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(22, 135, 37, 0.8)",
                    },
                  }}
                  disabled={kycApproving}
                  onClick={openApproveKycDialog}
                >
                  {kycApproving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Approve KYC"
                  )}
                </Button>
              ) : null}
            </Box>
          </Box>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0px 0 0px 0",
            }}
          />
          <Box
            sx={{
              mt: 4,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: kycData?.alternatemobileno
                  ? "repeat(5, 1fr)"
                  : "repeat(4, 1fr)",
              },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box>
              <Typography sx={labelSx}>User Name</Typography>
              <Typography sx={valueSx}>{userDetails.userName}</Typography>
            </Box>

            <Box>
              <Typography sx={labelSx}>Phone Number</Typography>
              <Typography sx={valueSx}>{userDetails.mobileNo}</Typography>
            </Box>

            {kycData?.alternatemobileno && (
              <Box>
                <Typography sx={labelSx}>Alternate Mobile Number</Typography>
                <Typography sx={valueSx}>
                  {formatMobileNumber(kycData.alternatemobileno)}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                gridColumn: {
                  xs: "1",
                  sm: kycData?.alternatemobileno ? "span 2" : "span 2",
                  md: "auto",
                },
              }}
            >
              <Typography sx={labelSx}>Email</Typography>
              <Typography
                sx={{
                  ...valueSx,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {userDetails.email || "N/A"}
              </Typography>
            </Box>

            <Box>
              <Typography sx={labelSx}>Joined Date</Typography>
              <Typography sx={valueSx}>
                {userDetails.userjoiningdate}
              </Typography>
            </Box>

            <Box>
              <Typography sx={labelSx}>App version</Typography>
              <Typography sx={valueSx}>
                {userDetails.appVersion || "N/A"}
              </Typography>
            </Box>
          </Box>
          {userDetails.isBanned && userDetails.bannedReason && (
            <div className="row mt-3">
              <div className="col-md-12">
                <Typography sx={labelSx}>Ban Reason</Typography>
                <Typography sx={{ ...valueSx, color: "rgba(197, 0, 0, 1)" }}>
                  {userDetails.bannedReason}
                </Typography>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {userBankDetailsDisplay != null ? (
        <Card sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                ...sectionTitleSx,
                color: "rgba(22, 67, 135, 1)",
                mb: 0,
              }}
            >
              Bank details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                mb: 0,
                color: "text.secondary",
                fontSize: "13px",
              }}
            >
              Linked payout / refund account from the user profile.
            </Typography>
            <hr
              style={{
                border: "none",
                borderTop: "1px solid rgb(161, 160, 160)",
                margin: "12px 0 0 0",
              }}
            />
            <Box
              sx={{
                mt: 3,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: { xs: 2.5, md: 3 },
                columnGap: 4,
              }}
            >
              <Box>
                <Typography sx={labelSx}>Account holder name</Typography>
                <Typography sx={valueSx}>
                  {userBankDetailsDisplay.holder || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Bank name</Typography>
                <Typography sx={valueSx}>
                  {userBankDetailsDisplay.bankName || "—"}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                <Typography sx={labelSx}>Account number</Typography>
                <Typography
                  sx={{
                    ...valueSx,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    fontSize: "15px",
                    letterSpacing: "0.03em",
                    wordBreak: "break-all",
                  }}
                >
                  {userBankDetailsDisplay.account || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>IFSC code</Typography>
                <Typography
                  sx={{
                    ...valueSx,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                    letterSpacing: "0.04em",
                  }}
                >
                  {userBankDetailsDisplay.ifsc || "—"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>UPI ID</Typography>
                <Typography
                  sx={{
                    ...valueSx,
                    fontWeight: 600,
                    wordBreak: "break-all",
                  }}
                >
                  {userBankDetailsDisplay.upi || "—"}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* KYC & Compliance */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              ...sectionTitleSx,
              color: "rgba(22, 67, 135, 1)",
              marginBottom: 0,
            }}
          >
            KYC & Compliance
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0px 0 15px 0",
            }}
          />

          {kycLoading ? (
            <CustomLoading message="Fetching KYC Data..." />
          ) : kycList.length > 0 ? (
            <Box>
              {/* KYC attempts – all transaction IDs for debugging */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "rgba(22, 67, 135, 1)",
                    mb: 1.5,
                  }}
                >
                  KYC attempts
                </Typography>
                <Table
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Transaction ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Transaction date (IST)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Verification status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>KYC status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kycList.map((k, idx) => (
                      <TableRow key={k.kycid}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell
                          sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                        >
                          {k.kycdetails?.transactionId ?? "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.9rem" }}>
                          {formatTransactionIdAsIST(
                            k.kycdetails?.transactionId,
                          )}
                        </TableCell>
                        <TableCell>
                          {k?.kycstatus === 1
                            ? "Verified"
                            : k?.kycstatus === 0
                              ? "Failed"
                              : k?.kycstatus === 2
                                ? "Needs review"
                                : "Pending"}
                        </TableCell>
                        <TableCell>{k.kycdetails?.status ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Main KYC details (from verified or latest attempt) */}
              {kycData?.kycdetails ? (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "rgba(22, 67, 135, 1)",
                      mb: 2,
                    }}
                  >
                    Verified / current KYC details
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography sx={labelSx}>Transaction ID</Typography>
                      <Typography
                        sx={{
                          ...valueSx,
                          fontWeight: 500,
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                        }}
                      >
                        {kycData.kycdetails?.transactionId ?? "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={labelSx}>
                        Transaction date (IST)
                      </Typography>
                      <Typography
                        sx={{ ...valueSx, fontWeight: 500, fontSize: "0.9rem" }}
                      >
                        {formatTransactionIdAsIST(
                          kycData.kycdetails?.transactionId,
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Full Name</Typography>
                      <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                        {kycData.kycdetails.aadhaarname}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Aadhaar Number</Typography>
                      <Typography
                        sx={{
                          ...valueSx,
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        {kycData.kycdetails.maskedaadhaarnumber}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Date of Birth</Typography>
                      <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                        {kycData.kycdetails.aadhaardob}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={labelSx}>Gender</Typography>
                      <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                        {kycData.kycdetails.aadhaargender === "M"
                          ? "Male"
                          : "Female"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Driving License Information Section */}
                  {(kycData.kycdetails.dl_ocr_idnumber ||
                    kycData.kycdetails.dl_ocr_fullname ||
                    kycData.kycdetails.dl_ocr_dateofissue) && (
                    <Box sx={{ mb: 4, mt: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: "rgba(22, 67, 135, 1)",
                          mb: 2,
                          borderBottom: "2px solid rgba(22, 67, 135, 0.2)",
                          pb: 1,
                        }}
                      >
                        Driving License Information
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                          },
                          gap: 3,
                          mb: 3,
                        }}
                      >
                        {kycData.kycdetails.dl_ocr_idnumber && (
                          <Box>
                            <Typography sx={labelSx}>License Number</Typography>
                            <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                              {kycData.kycdetails.dl_ocr_idnumber}
                            </Typography>
                          </Box>
                        )}

                        {kycData.kycdetails.dl_ocr_fullname && (
                          <Box>
                            <Typography sx={labelSx}>
                              License Holder Name
                            </Typography>
                            <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                              {kycData.kycdetails.dl_ocr_fullname}
                            </Typography>
                          </Box>
                        )}

                        {kycData.kycdetails.dl_ocr_dateofissue && (
                          <Box>
                            <Typography sx={labelSx}>Date of Issue</Typography>
                            <Typography sx={{ ...valueSx, fontWeight: 500 }}>
                              {kycData.kycdetails.dl_ocr_dateofissue}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Address Information Section */}
                  <Box sx={{ mb: 4, mt: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "rgba(22, 67, 135, 1)",
                        mb: 2,
                        borderBottom: "2px solid rgba(22, 67, 135, 0.2)",
                        pb: 1,
                      }}
                    >
                      Address Information
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      <Box>
                        <Typography sx={labelSx}>Current Address</Typography>
                        <Typography sx={{ ...valueSx, lineHeight: 1.6 }}>
                          {kycData.house}, {kycData.addressline1},{" "}
                          {kycData.addressline2}, {kycData.city},{" "}
                          {kycData.state} - {kycData.pincode}, {kycData.country}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography sx={labelSx}>Aadhaar Address</Typography>
                        <Typography sx={{ ...valueSx, lineHeight: 1.6 }}>
                          {kycData.kycdetails.aadhaaraddress}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Documents Section */}
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "rgba(22, 67, 135, 1)",
                        mb: 3,
                        borderBottom: "2px solid rgba(22, 67, 135, 0.2)",
                        pb: 1,
                      }}
                    >
                      Documents & Verification
                    </Typography>

                    <div className="row g-4">
                      {/* Selfie Image */}
                      {kycData.kycdetails.selfie_image && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Selfie Verification
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "rgba(22, 67, 135, 0.5)",
                                },
                              }}
                            >
                              <img
                                src={resolvePreviewSrc(
                                  kycData.kycdetails.selfie_image,
                                )}
                                alt="Selfie Verification"
                                style={{
                                  width: "180px",
                                  height: "180px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                onClick={() =>
                                  window.open(
                                    resolvePreviewSrc(
                                      kycData.kycdetails.selfie_image,
                                    ),
                                    "_blank",
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </div>
                      )}

                      {/* Aadhaar Document */}
                      {kycData.kycdetails.aadhaarfile && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Aadhaar Document
                            </Typography>
                            <Box
                              sx={{
                                width: "120px",
                                height: "120px",
                                backgroundColor: "rgba(22, 67, 135, 0.1)",
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                                border: "2px solid rgba(22, 67, 135, 0.2)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "2rem",
                                  color: "rgba(22, 67, 135, 0.7)",
                                }}
                              >
                                📄
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: "rgba(22, 67, 135, 1)",
                                "&:hover": {
                                  backgroundColor: "rgba(22, 67, 135, 0.8)",
                                },
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                              onClick={() =>
                                window.open(
                                  resolvePreviewSrc(
                                    kycData.kycdetails.aadhaarfile,
                                  ),
                                  "_blank",
                                )
                              }
                            >
                              View PDF
                            </Button>
                          </Box>
                        </div>
                      )}

                      {/* Driving License Front */}
                      {kycData.kycdetails.dl_ocr_frontimage && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Driving License - Front
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "rgba(22, 67, 135, 0.5)",
                                },
                              }}
                            >
                              <img
                                src={resolvePreviewSrc(
                                  kycData.kycdetails.dl_ocr_frontimage,
                                )}
                                alt="Driving License Front"
                                style={{
                                  width: "180px",
                                  height: "120px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                onClick={() =>
                                  window.open(
                                    resolvePreviewSrc(
                                      kycData.kycdetails.dl_ocr_frontimage,
                                    ),
                                    "_blank",
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </div>
                      )}

                      {/* Driving License Back */}
                      {kycData.kycdetails.dl_ocr_backimage && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Driving License - Back
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "rgba(22, 67, 135, 0.5)",
                                },
                              }}
                            >
                              <img
                                src={resolvePreviewSrc(
                                  kycData.kycdetails.dl_ocr_backimage,
                                )}
                                alt="Driving License Back"
                                style={{
                                  width: "180px",
                                  height: "120px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                onClick={() =>
                                  window.open(
                                    resolvePreviewSrc(
                                      kycData.kycdetails.dl_ocr_backimage,
                                    ),
                                    "_blank",
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </div>
                      )}

                      {/* Aadhaar OCR Front Image */}
                      {kycData.kycdetails.aadhaarocr_frontimg && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Aadhaar OCR - Front
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "rgba(22, 67, 135, 0.5)",
                                },
                              }}
                            >
                              <img
                                src={resolvePreviewSrc(
                                  kycData.kycdetails.aadhaarocr_frontimg!,
                                )}
                                alt="Aadhaar OCR Front"
                                style={{
                                  width: "180px",
                                  height: "180px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                onClick={() =>
                                  window.open(
                                    resolvePreviewSrc(
                                      kycData.kycdetails.aadhaarocr_frontimg!,
                                    ),
                                    "_blank",
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </div>
                      )}

                      {/* Aadhaar OCR Back Image */}
                      {kycData.kycdetails.aadhaarocr_backimg && (
                        <div className="col-lg-4 col-md-6">
                          <Box
                            sx={{
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 2,
                              backgroundColor: "#fafafa",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                color: "rgba(22, 67, 135, 1)",
                                mb: 2,
                                textAlign: "center",
                              }}
                            >
                              Aadhaar OCR - Back
                            </Typography>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid #e0e0e0",
                                cursor: "pointer",
                                "&:hover": {
                                  borderColor: "rgba(22, 67, 135, 0.5)",
                                },
                              }}
                            >
                              <img
                                src={resolvePreviewSrc(
                                  kycData.kycdetails.aadhaarocr_backimg!,
                                )}
                                alt="Aadhaar OCR Back"
                                style={{
                                  width: "180px",
                                  height: "180px",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                                onClick={() =>
                                  window.open(
                                    resolvePreviewSrc(
                                      kycData.kycdetails.aadhaarocr_backimg!,
                                    ),
                                    "_blank",
                                  )
                                }
                              />
                            </Box>
                          </Box>
                        </div>
                      )}

                      {/* Address Documents */}
                      {kycData.addressdocument &&
                        (() => {
                          const addressDocs = parseFilePaths(
                            kycData.addressdocument!,
                          );
                          return addressDocs.length > 0 ? (
                            <>
                              {addressDocs.map((filePath, index) => (
                                <div key={index} className="col-lg-4 col-md-6">
                                  <Box
                                    sx={{
                                      p: 2,
                                      border: "1px solid #e0e0e0",
                                      borderRadius: 2,
                                      backgroundColor: "#fafafa",
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.3s ease",
                                      "&:hover": {
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        transform: "translateY(-2px)",
                                      },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        color: "rgba(22, 67, 135, 1)",
                                        mb: 2,
                                        textAlign: "center",
                                      }}
                                    >
                                      Address Document{" "}
                                      {addressDocs.length > 1
                                        ? `(${index + 1})`
                                        : ""}
                                    </Typography>
                                    {isPdfFile(filePath) ? (
                                      <>
                                        <Box
                                          sx={{
                                            width: "120px",
                                            height: "120px",
                                            backgroundColor:
                                              "rgba(22, 67, 135, 0.1)",
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 2,
                                            border:
                                              "2px solid rgba(22, 67, 135, 0.2)",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "2rem",
                                              color: "rgba(22, 67, 135, 0.7)",
                                            }}
                                          >
                                            📄
                                          </Typography>
                                        </Box>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          sx={{
                                            backgroundColor:
                                              "rgba(22, 67, 135, 1)",
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(22, 67, 135, 0.8)",
                                            },
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                          }}
                                          onClick={() =>
                                            window.open(
                                              resolvePreviewSrc(filePath),
                                              "_blank",
                                            )
                                          }
                                        >
                                          View PDF
                                        </Button>
                                      </>
                                    ) : (
                                      <Box
                                        sx={{
                                          position: "relative",
                                          borderRadius: 2,
                                          overflow: "hidden",
                                          border: "2px solid #e0e0e0",
                                          cursor: "pointer",
                                          "&:hover": {
                                            borderColor:
                                              "rgba(22, 67, 135, 0.5)",
                                          },
                                        }}
                                      >
                                        <img
                                          src={resolvePreviewSrc(filePath)}
                                          alt={`Address Document ${index + 1}`}
                                          style={{
                                            width: "180px",
                                            height: "180px",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                          onClick={() =>
                                            window.open(
                                              resolvePreviewSrc(filePath),
                                              "_blank",
                                            )
                                          }
                                        />
                                      </Box>
                                    )}
                                  </Box>
                                </div>
                              ))}
                            </>
                          ) : null;
                        })()}
                    </div>
                  </Box>
                </>
              ) : (
                <Typography
                  sx={{ color: "text.secondary", fontStyle: "italic" }}
                >
                  No verified KYC details. Use the attempts table above for
                  debugging.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                No KYC data available for this user.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Ride History (from bookings) */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              ...sectionTitleSx,
              color: "rgba(22, 67, 135, 1)",
              marginBottom: 0,
            }}
          >
            Ride History
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0px 0 15px 0",
            }}
          />
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            {rideRows.length > 0 ? (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(191, 229, 255, 1)" }}>
                    {[
                      "EV Model",
                      "Trip Start Date",
                      "Trip End Date",
                      "Distance",
                      "Amount Paid",
                    ].map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          ...tableHeaderSx,
                          fontWeight: "bold",
                          fontSize: "15px",
                          color: "#333",
                          cursor: "pointer",
                          backgroundColor: "rgba(191, 229, 255, 1)",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {head}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rideRows.map((ride, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ride.evModel}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ride.tripStartDate}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ride.tripEndDate}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ride.distance}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ride.amountPaid}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "16px", fontWeight: "500", mb: 1 }}
                >
                  No Ride History
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", color: "text.secondary" }}
                >
                  This user has not completed any rides yet.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={2}
            sx={{ mb: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                ...sectionTitleSx,
                color: "rgba(22, 67, 135, 1)",
                marginBottom: 0,
              }}
            >
              Coupons & Credits History
            </Typography>
            {canManageUserCoupons ? (
              <Button
                variant="contained"
                disableElevation
                onClick={() => openIssueCouponCreate()}
                sx={issueCouponPrimaryButtonSx}
              >
                Issue Custom Coupon
              </Button>
            ) : null}
          </Box>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0 0 15px 0",
            }}
          />
          {userPromoCodesError ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              onClose={() => setUserPromoCodesError(null)}
            >
              {userPromoCodesError}
            </Alert>
          ) : null}
          <Box
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            {userPromoCodesLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 5,
                }}
              >
                <CircularProgress size={36} />
              </Box>
            ) : userPromoCodes.length > 0 ? (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(191, 229, 255, 1)" }}>
                    {(canManageUserCoupons
                      ? [
                          "Coupon Code",
                          "Type",
                          "Discount Value",
                          "Validity Dates",
                          "Status",
                          "Action",
                        ]
                      : [
                          "Coupon Code",
                          "Type",
                          "Discount Value",
                          "Validity Dates",
                          "Status",
                        ]
                    ).map((head) => (
                      <TableCell
                        key={head}
                        sx={{
                          ...tableHeaderSx,
                          fontWeight: "bold",
                          fontSize: "15px",
                          color: "#333",
                          backgroundColor: "rgba(191, 229, 255, 1)",
                          ...(head === "Status" || head === "Action"
                            ? { textAlign: "center" }
                            : {}),
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={
                            head === "Status" || head === "Action"
                              ? "center"
                              : "space-between"
                          }
                          gap={0.5}
                        >
                          <span>{head}</span>
                          <Box
                            component="span"
                            sx={{
                              color: "#9e9e9e",
                              fontSize: "0.75rem",
                              lineHeight: 1,
                              userSelect: "none",
                            }}
                            aria-hidden
                          >
                            ↑
                          </Box>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userPromoCodes.map((row, idx) => {
                    const typeRaw = row.discounttype ?? row.discountType;
                    const typeForLookup =
                      typeRaw === undefined || typeRaw === null
                        ? undefined
                        : (typeRaw as string | number);
                    let typeLabel = getDiscountTypeDisplayName(typeForLookup);
                    if (!typeLabel) {
                      if (typeRaw === 2 || typeRaw === "2")
                        typeLabel = "Percentage";
                      else if (typeRaw === 0 || typeRaw === "0")
                        typeLabel = "Flat amount";
                      else typeLabel = String(typeRaw ?? "—");
                    }
                    const effectiveStatusCode = resolvePromoStatusCodeValue(
                      row,
                      expiredPromoStatusCodeValue,
                    );
                    const statusLabel =
                      getPromoStatusDisplayName(effectiveStatusCode) ||
                      (effectiveStatusCode !== "" ? effectiveStatusCode : "—");
                    const rowKey = String(row.couponid ?? row.couponId ?? idx);
                    const couponId = promoCouponIdString(row);
                    return (
                      <TableRow
                        key={rowKey}
                        sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                      >
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontSize: "15px",
                            color: "#555",
                            fontWeight: 500,
                          }}
                        >
                          {couponCouponCode(row)}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontSize: "15px",
                            color: "#555",
                            fontWeight: 500,
                          }}
                        >
                          {typeLabel}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontSize: "15px",
                            color: "#555",
                            fontWeight: 500,
                          }}
                        >
                          {promoDiscountValueDisplay(row)}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...tableCellSx,
                            fontSize: "15px",
                            color: "#555",
                            fontWeight: 500,
                          }}
                        >
                          {promoValidityDisplay(row)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellSx, textAlign: "center" }}>
                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={couponStatusChipSx(statusLabel)}
                          />
                        </TableCell>
                        {canManageUserCoupons ? (
                          <TableCell
                            sx={{ ...tableCellSx, textAlign: "center" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                variant="text"
                                size="small"
                                sx={{ minWidth: 36, p: 0.5 }}
                                disabled={!couponId}
                                onClick={() => {
                                  if (couponId)
                                    void openEditIssueCoupon(couponId);
                                }}
                                aria-label="Edit coupon"
                              >
                                <img
                                  src={EditIcon}
                                  alt=""
                                  width={28}
                                  height={28}
                                />
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                sx={{ minWidth: 36, p: 0.5 }}
                                disabled={!couponId}
                                onClick={() => openDeleteUserCoupon(row)}
                                aria-label="Delete coupon"
                              >
                                <img
                                  src={DeleteIcon}
                                  alt=""
                                  width={28}
                                  height={28}
                                />
                              </Button>
                            </Box>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "16px", fontWeight: 500, mb: 1 }}
                >
                  No coupons or credits yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", color: "text.secondary" }}
                >
                  Issue a custom coupon using the button above when available.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Payments & Penalties */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              ...sectionTitleSx,
              color: "rgba(22, 67, 135, 1)",
              marginBottom: 0.5,
            }}
          >
            Payments & Penalties
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0px 0 25px 0px",
            }}
          />
          <Box display="flex" gap={3}>
            {[
              {
                label: "Total Paid",
                value: `₹${paymentsSummary.totalPaid.toLocaleString()}`,
                bg: "rgba(234, 236, 255, 1)",
                color: "rgba(80, 80, 80, 1)",
              },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  minWidth: 140,
                  borderRadius: 2,
                  bgcolor: item.bg,
                  color: item.color,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "15px", opacity: 0.9 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Support Summary */}
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              ...sectionTitleSx,
              color: "rgba(22, 67, 135, 1)",
              marginBottom: 0.5,
            }}
          >
            Support Summary
          </Typography>
          <hr
            style={{
              border: "none",
              borderTop: "1px solid rgb(161, 160, 160)",
              margin: "0px 0 20px 0",
            }}
          />
          <Box
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            {transformedSupportTickets.length > 0 ? (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(191, 229, 255, 1)" }}>
                    {["Ticket ID", "Ticket Type", "Raised Date", "Status"].map(
                      (head) => (
                        <TableCell
                          key={head}
                          sx={{
                            ...tableHeaderSx,
                            fontWeight: "bold",
                            fontSize: "15px",
                            color: "#333",
                            cursor: "pointer",
                            backgroundColor: "rgba(191, 229, 255, 1)",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            {head}
                          </Box>
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transformedSupportTickets.map((ticket, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                    >
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ticket.ticketId}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ticket.ticketType}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ticket.raisedDate}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          fontSize: "15px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {ticket.status}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "16px", fontWeight: "500", mb: 1 }}
                >
                  No Support Tickets
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", color: "text.secondary" }}
                >
                  This user has not raised any support tickets yet.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Save & Back Buttons */}
      <Box
        textAlign="end"
        sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}
      >
        <Button
          variant="contained"
          onClick={handleSaveStatus}
          disabled={
            isSavingStatus ||
            !selectedStatus ||
            selectedStatus === userDetails.status
          }
          sx={{
            px: 4,
            py: 1.2,
            fontSize: "15px",
            borderRadius: 1.5,
            textTransform: "none",
            backgroundColor: "rgba(13, 44, 90, 1)",
            "&:hover": {
              backgroundColor: "rgba(13, 44, 90, 0.8)",
            },
          }}
        >
          {isSavingStatus ? "Saving..." : "Save"}
        </Button>
        <Button
          variant="contained"
          onClick={handleBack}
          sx={{
            px: 4,
            py: 1.2,
            fontSize: "15px",
            borderRadius: 1.5,
            textTransform: "none",
            backgroundColor: "rgba(13, 44, 90, 1)",
            "&:hover": {
              backgroundColor: "rgba(13, 44, 90, 0.8)",
            },
          }}
        >
          Back
        </Button>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog
          open={issueCouponOpen}
          onClose={(_, reason) => {
            if (issueCouponDetailLoading) return;
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              handleCloseIssueCoupon();
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 },
          }}
        >
          <DialogTitle
            component="div"
            sx={{
              m: 0,
              py: 1.75,
              px: 2,
              backgroundColor: issueCouponHeaderBlue,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 700,
              fontSize: "1.125rem",
            }}
          >
            {issueCouponEditIntent || issueCouponEditingId != null
              ? "Edit Coupon"
              : "Issue Custom Coupon"}
            <IconButton
              type="button"
              onClick={handleCloseIssueCoupon}
              disabled={issueCouponDetailLoading}
              size="small"
              aria-label="Close"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, pb: 2, pt: 0, position: "relative" }}>
            {issueCouponDetailLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 280,
                  py: 4,
                }}
              >
                <CircularProgress
                  size={40}
                  sx={{ color: issueCouponHeaderBlue }}
                />
              </Box>
            ) : (
              <Box sx={{ pt: 4, pb: 0 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="Coupon Code"
                    size="small"
                    fullWidth
                    value={issueCouponCode}
                    onChange={(e) => setIssueCouponCode(e.target.value)}
                    sx={issueCouponModalFieldSx}
                  />
                  <Box sx={issueCouponModalFieldSx}>
                    <SearchableSelectInput
                      field="issueDiscountType"
                      formData={{
                        issueDiscountType:
                          issueDiscountType !== null &&
                          issueDiscountType !== undefined
                            ? String(issueDiscountType)
                            : "",
                      }}
                      onChange={(_field, value) => setIssueDiscountType(value)}
                      options={couponDiscountTypeCodes.map((c) => ({
                        label: c.codedisplayname,
                        value: c.codevalue,
                      }))}
                      label="Discount Type"
                      placeholder="Select discount type"
                    />
                  </Box>
                  <TextField
                    label="Discount Value"
                    size="small"
                    fullWidth
                    value={issueDiscountValue}
                    onChange={(e) => setIssueDiscountValue(e.target.value)}
                    sx={issueCouponModalFieldSx}
                  />
                  <DatePicker
                    label="Expiry Date"
                    format="DD/MM/YYYY"
                    value={issueExpiryDate}
                    onChange={(v) => setIssueExpiryDate(v)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: issueCouponModalFieldSx,
                      },
                    }}
                  />
                </Box>
                {/* <TextField
                  label="Usage Limit"
                  size="small"
                  fullWidth
                  type="number"
                  value={1}
                  disabled
                  inputProps={{ min: 0 }}
                  sx={{ ...issueCouponModalFieldSx, mb: 2 }}
                /> */}
                <TextField
                  label="Reason"
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  value={issueReason}
                  onChange={(e) => setIssueReason(e.target.value)}
                  placeholder=""
                  sx={issueCouponModalFieldSx}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.5,
              pb: 2,
              pt: 1,
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="text"
              onClick={handleCloseIssueCoupon}
              disabled={issueCouponSubmitting || issueCouponDetailLoading}
              sx={{
                color: issueCouponHeaderBlue,
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(22, 67, 135, 0.6)",
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              disableElevation
              onClick={() => void handleSubmitIssueCoupon()}
              disabled={issueCouponSubmitting || issueCouponDetailLoading}
              sx={issueCouponPrimaryButtonSx}
            >
              {issueCouponSubmitting
                ? "Submitting..."
                : issueCouponEditingId != null
                  ? "Update"
                  : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      <Dialog
        open={approveKycDialogOpen}
        onClose={closeApproveKycDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1">
            Are you sure you want to approve KYC?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button onClick={closeApproveKycDialog} disabled={kycApproving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmApproveKyc()}
            disabled={kycApproving}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={couponDeleteDialogOpen}
        message={
          couponPendingDelete
            ? `Are you sure you want to delete promo code "${couponPendingDelete.codeName}"? This action cannot be undone.`
            : ""
        }
        loading={couponDeleteLoading}
        onCancel={cancelDeleteUserCoupon}
        onConfirm={() => void confirmDeleteUserCoupon()}
      />
    </Box>
  );
};

export default UserDetailsPage;
